/**
 * Filter Executor
 *
 * Executes filter sets against node collections
 */

import { GraphDataNode } from '../../types/graph.types';
import {
  FilterDefinition,
  FilterSet,
  FilterResult,
} from '../types';

/**
 * Execute a filter set against a collection of nodes
 *
 * All filters are combined with AND logic - a node must pass all filters
 *
 * @param nodes - Nodes to filter
 * @param filterSet - Active filters to apply
 * @param definitions - Filter definitions
 * @returns Filter result with nodes and metadata
 */
export function executeFilterSet(
  nodes: GraphDataNode[],
  filterSet: FilterSet,
  definitions: Record<string, FilterDefinition>
): FilterResult {
  const startTime = performance.now();
  const totalCount = nodes.length;

  // If no filters, return all nodes
  if (filterSet.length === 0) {
    return {
      nodes,
      totalCount,
      filteredCount: totalCount,
      executionTimeMs: performance.now() - startTime,
    };
  }

  // Filter nodes - all filters must pass (AND logic)
  const filteredNodes = nodes.filter((node) => {
    return filterSet.every((activeFilter) => {
      const definition = definitions[activeFilter.filterId];
      if (!definition) {
        console.warn(`Unknown filter: ${activeFilter.filterId}`);
        return true; // Unknown filter, pass through
      }

      try {
        return definition.execute(node, activeFilter.operator, activeFilter.value);
      } catch (error) {
        console.warn(
          `Error executing filter "${activeFilter.filterId}" on node ${node.nodeId}:`,
          error
        );
        return true; // On error, pass through
      }
    });
  });

  return {
    nodes: filteredNodes,
    totalCount,
    filteredCount: filteredNodes.length,
    executionTimeMs: performance.now() - startTime,
  };
}

/**
 * Cache key generator for memoization
 */
export function generateFilterCacheKey(filterSet: FilterSet): string {
  return filterSet
    .map((f) => `${f.filterId}:${f.operator}:${JSON.stringify(f.value)}`)
    .sort()
    .join('|');
}

